import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import AnimatedBackground from '../components/AnimatedBackground';
import ScrollReveal from '../components/ScrollReveal';
import { images } from '../data/images';
import { ITThematicImages } from '../components/ITThematicImages';
import { 
  Monitor, 
  Cloud, 
  Database, 
  Code2, 
  Shield, 
  Globe, 
  Handshake, 
  ArrowRight,
  ChevronRight
} from 'lucide-react';

const HomePage = () => {
  const { t } = useLanguage();
  const [isLoaded, setIsLoaded] = useState(false);

  const handleNavClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    // Trigger entrance animations
    const timer = setTimeout(() => setIsLoaded(true), 100);
    
    return () => {
      clearTimeout(timer);
    };
  }, []);

  const serviceIcons = {
    consulting: Monitor,
    cloud: Cloud,
    data: Database,
    software: Code2,
  };

  const serviceImages = {
    consulting: ITThematicImages.itConsulting,
    cloud: ITThematicImages.cloudInfrastructure,
    data: ITThematicImages.dataAnalytics,
    software: ITThematicImages.softwareDevelopment,
  };

  const trustIcons = [Shield, Globe, Handshake];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center bg-[#1E2A38] overflow-hidden">
        {/* Background Image with Overlay */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat transition-transform duration-[20s] ease-linear hover:scale-110"
          style={{ 
            backgroundImage: `url(${images.hero.background})`,
            filter: 'brightness(0.3) saturate(0.8)',
          }}
        />
        
        {/* Animated Particle Network */}
        <AnimatedBackground />
        
        {/* Gradient Overlays with mesh effect */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#1E2A38]/80 via-transparent to-[#1E2A38]/90" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#2B3A4D]/50 via-transparent to-[#0066CC]/10" />
        <div className="absolute inset-0 gradient-mesh opacity-30" />
        
        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-32 text-center">
          {/* Badge */}
          <div 
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full glass-blue border border-[#0066CC]/30 mb-8 transition-all duration-700 hover:scale-105 hover:shadow-lg hover:shadow-[#0066CC]/20 ${
              isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
            style={{ transitionDelay: '200ms' }}
          >
            <span className="text-[#0066CC] text-sm font-medium">Global Services s.r.o.</span>
            <span className="text-white/50">|</span>
            <span className="text-white/70 text-sm">French Subsidiary</span>
          </div>
          
          {/* Headline */}
          <h1 
            className={`text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-2 tracking-tight transition-all duration-700 ${
              isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
            style={{ transitionDelay: '300ms' }}
          >
            {t('hero.headline')}
          </h1>
          <h1 
            className={`text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight transition-all duration-700 ${
              isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
            style={{ transitionDelay: '400ms' }}
          >
            {t('hero.headlineLine2')}
          </h1>
          
          {/* Subheadline */}
          <p 
            className={`text-lg md:text-xl text-[#0066CC] font-medium mb-8 tracking-wide transition-all duration-700 ${
              isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
            style={{ transitionDelay: '500ms' }}
          >
            {t('hero.subheadline')}
          </p>
          
          {/* Description */}
          <p 
            className={`text-white/70 text-lg max-w-3xl mx-auto mb-12 leading-relaxed transition-all duration-700 ${
              isLoaded ? 'opacity-100' : 'opacity-0'
            }`}
            style={{ transitionDelay: '600ms' }}
          >
            {t('hero.description')}
          </p>
          
          {/* CTA Buttons */}
          <div 
            className={`flex flex-col sm:flex-row items-center justify-center gap-4 transition-all duration-700 ${
              isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
            style={{ transitionDelay: '800ms' }}
          >
            <Link to="/services" onClick={handleNavClick}>
              <Button 
                size="lg" 
                className="bg-[#0066CC] hover:bg-[#0055AA] text-white px-8 py-6 text-base group transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-[#0066CC]/40 animate-glow-pulse perspective-container"
              >
                {t('hero.cta1')}
                <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-2 group-hover:-translate-y-0.5 transition-transform duration-300" />
              </Button>
            </Link>
            <Link to="/contact" onClick={handleNavClick}>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-white/30 text-white hover:bg-white/10 px-8 py-6 text-base transition-all duration-300 hover:-translate-y-1 hover:shadow-lg glass"
              >
                {t('hero.cta2')}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Services Overview Section */}
      <section className="py-24 bg-[#F8F9FA] relative overflow-hidden">
        {/* Subtle background pattern */}
        <div 
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%232B3A4D' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          {/* Section Header */}
          <ScrollReveal>
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-[#2B3A4D] mb-4">
                {t('services.title')}
              </h2>
              <p className="text-[#6B7B8C] text-lg max-w-2xl mx-auto">
                {t('services.subtitle')}
              </p>
            </div>
          </ScrollReveal>
          
          {/* Services Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {t('services.items').map((service, index) => {
              const IconComponent = serviceIcons[service.id];
              const serviceImage = serviceImages[service.id];
              return (
                <ScrollReveal key={service.id} delay={index * 100}>
                  <Link to="/services" onClick={handleNavClick}>
                    <Card 
                      className="group bg-white border-transparent hover:border-[#0066CC] transition-all duration-300 hover:shadow-xl hover:-translate-y-2 cursor-pointer overflow-hidden h-full perspective-container card-3d"
                    >
                      {/* Image Header */}
                      <div className="relative h-48 overflow-hidden transform-3d">
                        {typeof serviceImage === 'object' ? (
                          <div className="w-full h-full transition-transform duration-700 group-hover:scale-110 group-hover:rotate-1">
                            {serviceImage}
                          </div>
                        ) : (
                          <img 
                            src={serviceImage}
                            alt={service.title}
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                            loading="lazy"
                          />
                        )}
                        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#2B3A4D]/30 to-[#2B3A4D]/80 transition-opacity duration-300 group-hover:opacity-80" />
                        <div className="absolute bottom-4 left-6 transform transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3">
                          <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center animate-glow-pulse">
                            <IconComponent className="w-6 h-6 text-white" />
                          </div>
                        </div>
                      </div>
                      
                      <CardContent className="p-6 transform-3d">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <h3 className="text-xl font-semibold text-[#2B3A4D] mb-2 group-hover:text-[#0066CC] transition-colors duration-200">
                              {service.title}
                            </h3>
                            <p className="text-[#6B7B8C] leading-relaxed group-hover:text-[#2B3A4D] transition-colors duration-300">
                              {service.shortDesc}
                            </p>
                          </div>
                          <ChevronRight className="w-5 h-5 text-[#6B7B8C] group-hover:text-[#0066CC] group-hover:translate-x-2 transition-all duration-300 mt-1 flex-shrink-0" />
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                </ScrollReveal>
              );
            })}
          </div>
          
          {/* View All Link */}
          <ScrollReveal delay={400}>
            <div className="text-center mt-12">
              <Link to="/services" onClick={handleNavClick}>
                <Button 
                  variant="ghost" 
                  className="text-[#0066CC] hover:bg-[#0066CC]/10 group"
                >
                  {t('hero.cta1')}
                  <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform duration-200" />
                </Button>
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Trust Indicators Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <ScrollReveal>
            <h2 className="text-3xl md:text-4xl font-bold text-[#2B3A4D] text-center mb-16">
              {t('trust.title')}
            </h2>
          </ScrollReveal>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {t('trust.items').map((item, index) => {
              const IconComponent = trustIcons[index];
              return (
                <ScrollReveal key={index} delay={index * 150}>
                  <div 
                    className="text-center p-8 rounded-2xl hover:bg-[#F8F9FA] transition-all duration-300 group perspective-container card-3d"
                  >
                    <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#0066CC]/10 flex items-center justify-center group-hover:bg-[#0066CC] group-hover:scale-110 group-hover:rotate-6 transition-all duration-300 animate-glow-pulse">
                      <IconComponent className="w-8 h-8 text-[#0066CC] group-hover:text-white transition-colors duration-300" />
                    </div>
                    <h3 className="text-xl font-semibold text-[#2B3A4D] mb-3">
                      {item.title}
                    </h3>
                    <p className="text-[#6B7B8C] leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </ScrollReveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Banner Section */}
      <section className="relative py-24 bg-[#2B3A4D] overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-10"
          style={{ backgroundImage: `url(${images.patterns.geometric})` }}
        />
        
        {/* Animated Background */}
        <AnimatedBackground className="opacity-30" />
        
        <ScrollReveal>
          <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-8">
              {t('cta.title')}
            </h2>
            <Link to="/contact" onClick={handleNavClick}>
              <Button 
                size="lg" 
                className="bg-[#0066CC] hover:bg-[#0055AA] text-white px-10 py-6 text-base group transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-[#0066CC]/30"
              >
                {t('cta.button')}
                <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform duration-200" />
              </Button>
            </Link>
          </div>
        </ScrollReveal>
      </section>
    </div>
  );
};

export default HomePage;
